import React, { useState, useEffect } from "react";
import { Plus, Search, Edit, Trash2, FileText, AlertCircle, Loader2, Download, Copy, Pin, Upload, Printer, Archive, ArchiveRestore } from "lucide-react";
import { useAgendas } from "../hooks/useAgendas";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import AgendaModal from "../components/modals/AgendaModal";
import UploadAgendaModal from "../components/modals/UploadAgendaModal";
import { exportAgendaToWord, printAgenda } from "../utils/agendaExport";

export default function Meetings() {
  const {
    agendas,
    loading,
    error,
    updateAgenda,
    deleteAgenda
  } = useAgendas();

  const { currentUser } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("agendas"); // "agendas" or "history"
  
  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedAgenda, setSelectedAgenda] = useState(null);
  
  // Admin role check
  const [isAdmin, setIsAdmin] = useState(false);
  const [companyLogo, setCompanyLogo] = useState("");

  useEffect(() => {
    if (!currentUser) return;
    const q = query(collection(db, "users"), where("email", "==", currentUser.email));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        const roles = data.roles || [];
        setIsAdmin(roles.includes("Admin"));
      } else {
        setIsAdmin(false);
      }
    }, (err) => {
      console.error("Error loading user roles in meetings page:", err);
    });
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    const unsubscribeLogo = onSnapshot(collection(db, "company_logos"), (snapshot) => {
      let activeUrl = "";
      let longarmUrl = "";
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.name?.toLowerCase().includes("longarm")) {
          longarmUrl = data.url;
        }
        if (data.status === "Active" || data.active || data.isActive) {
          activeUrl = data.url;
        }
      });
      setCompanyLogo(longarmUrl || activeUrl || "https://jimini-logistics.web.app/favicon.png");
    }, (err) => {
      console.error("Error loading company logo on meetings page:", err);
    });
    return () => unsubscribeLogo();
  }, []);

  const isOlderThanTwoWeeks = (dateString) => {
    if (!dateString) return false;
    const agendaDate = new Date(dateString + "T00:00:00");
    if (isNaN(agendaDate.getTime())) return false;
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    twoWeeksAgo.setHours(0, 0, 0, 0);
    return agendaDate < twoWeeksAgo;
  };

  const handleOpenAddModal = () => {
    setSelectedAgenda(null);
    setModalOpen(true);
  };

  const handleOpenEditModal = (agenda) => {
    if (agenda.isHistorical) {
      // Historical agendas don't need tabular editing
      return;
    }
    setSelectedAgenda(agenda);
    setModalOpen(true);
  };

  const handleDuplicateAgenda = (agenda) => {
    if (agenda.isHistorical) return;
    setSelectedAgenda({
      ...agenda,
      isDuplicate: true
    });
    setModalOpen(true);
  };

  const handleArchiveAgenda = async (agenda) => {
    try {
      await updateAgenda(agenda.id, { 
        archived: true, 
        archivedAt: new Date().toISOString() 
      });
    } catch (err) {
      console.error("Error archiving agenda:", err);
      window.alert("Failed to archive agenda: " + (err.message || "Unknown error"));
    }
  };

  const handleUnarchiveAgenda = async (agenda) => {
    try {
      await updateAgenda(agenda.id, { 
        archived: false, 
        archivedAt: null 
      });
    } catch (err) {
      console.error("Error restoring agenda:", err);
      window.alert("Failed to restore agenda: " + (err.message || "Unknown error"));
    }
  };

  const handleDeleteAgenda = async (agenda) => {
    if (activeTab === "history" && !isAdmin) {
      window.alert("You must have the Admin role to delete historical/logged agendas.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete "${agenda.title}"?`)) {
      return;
    }
    try {
      await deleteAgenda(agenda.id);
    } catch (err) {
      console.error("Error deleting agenda:", err);
    }
  };

  const handleExportWord = (agenda) => {
    exportAgendaToWord(agenda, companyLogo);
  };

  const handlePrintAgenda = (agenda) => {
    printAgenda(agenda, companyLogo);
  };

  // Filter and sort meetings based on selected tab
  const tabAgendas = agendas.filter(agenda => {
    if (activeTab === "agendas") {
      // Show created non-archived agendas (including templates) that are not older than 2 weeks
      return !agenda.isHistorical && !agenda.archived && (agenda.isTemplate || !isOlderThanTwoWeeks(agenda.date));
    } else {
      // History subtab: Show everything (created + historical uploads + archived)
      return true;
    }
  });

  const sortedAgendas = [...tabAgendas].sort((a, b) => {
    // templates pinned first inside active agendas tab
    if (activeTab === "agendas") {
      if (a.isTemplate && !b.isTemplate) return -1;
      if (!a.isTemplate && b.isTemplate) return 1;
    }
    return new Date(b.date) - new Date(a.date);
  });

  const filteredAgendas = sortedAgendas.filter(agenda => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (agenda.title || "").toLowerCase().includes(searchLower) ||
      (agenda.organizer || "").toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="animate-fade-in" style={{ padding: "1.5rem" }}>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
        <div>
          <h1 className="page-title" style={{ margin: 0, fontSize: "1.75rem", fontWeight: 700, color: "var(--text-primary)" }}>Meetings & Agendas</h1>
          <p className="text-secondary" style={{ margin: "0.25rem 0 0 0", fontSize: "0.875rem" }}>
            Track Business Development meetings, opportunities review, and action plans.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button className="btn btn-secondary" onClick={() => setUploadModalOpen(true)}>
            <Upload size={18} style={{ marginRight: "0.5rem" }} /> Upload Agendas
          </button>
          <button className="btn btn-primary" onClick={handleOpenAddModal}>
            <Plus size={18} style={{ marginRight: "0.5rem" }} /> Create Agenda
          </button>
        </div>
      </div>

      {/* Tab Selectors */}
      <div className="tab-container" style={{ display: "flex", borderBottom: "1px solid var(--border-color)", marginBottom: "1.5rem", gap: "1rem" }}>
        <button 
          className={`tab-btn ${activeTab === 'agendas' ? 'active' : ''}`}
          onClick={() => setActiveTab('agendas')}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'agendas' ? '2px solid var(--primary)' : '2px solid transparent',
            color: activeTab === 'agendas' ? 'var(--text-primary)' : 'var(--text-secondary)',
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Active Agendas
        </button>
        <button 
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: activeTab === 'history' ? '2px solid var(--primary)' : '2px solid transparent',
            color: activeTab === 'history' ? 'var(--text-primary)' : 'var(--text-secondary)',
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          History
        </button>
      </div>

      {/* Toolbar */}
      <div className="card glass-card" style={{ padding: "1rem", marginBottom: "1.5rem", display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
        <div className="input-with-icon" style={{ flex: 1, minWidth: "250px" }}>
          <Search className="input-icon" size={18} />
          <input
            type="text"
            className="form-input icon-padding"
            placeholder="Search meetings by topic or organizer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
          <Loader2 className="spinner-icon" size={32} style={{ animation: "spin 1s linear infinite" }} />
        </div>
      ) : error ? (
        <div className="card glass-card" style={{ padding: "2rem", textAlign: "center", color: "var(--danger)" }}>
          <AlertCircle size={36} style={{ marginBottom: "0.5rem" }} />
          <p>{error}</p>
        </div>
      ) : filteredAgendas.length === 0 ? (
        <div className="card glass-card" style={{ padding: "3rem 1rem", textAlign: "center", color: "var(--text-secondary)" }}>
          <FileText size={48} style={{ opacity: 0.3, marginBottom: "1rem" }} />
          <h3>No Records Found</h3>
          <p style={{ fontSize: "0.875rem" }}>
            {activeTab === 'agendas' 
              ? 'Create a meeting agenda to get started.' 
              : 'Upload historical PDF/Word documents to view them in the log.'}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ width: "160px", backgroundColor: "#17365D", color: "white" }}>Date</th>
                <th style={{ backgroundColor: "#17365D", color: "white" }}>Topic / Title</th>
                <th style={{ width: "150px", backgroundColor: "#17365D", color: "white" }}>Organizer</th>
                <th style={{ width: "100px", textAlign: "center", backgroundColor: "#17365D", color: "white" }}>Attendees</th>
                <th style={{ width: "160px", backgroundColor: "#17365D", color: "white" }}>Attachments</th>
                <th style={{ width: "180px", textAlign: "center", backgroundColor: "#17365D", color: "white" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAgendas.map((agenda) => (
                <tr key={agenda.id} style={agenda.isTemplate ? { backgroundColor: "rgba(0, 180, 216, 0.05)" } : {}}>
                  <td style={{ fontWeight: 600, color: agenda.isTemplate ? "var(--primary)" : "var(--text-primary)" }}>
                    {agenda.isTemplate ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", color: "var(--primary)", fontSize: "0.8rem" }}>
                        <Pin size={12} style={{ transform: "rotate(45deg)" }} /> Pinned Template
                      </span>
                    ) : (
                      agenda.date ? new Date(agenda.date + "T00:00:00").toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : "N/A"
                    )}
                  </td>
                  <td>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      {agenda.isHistorical ? (
                        <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                          {agenda.title} <span style={{ fontSize: "0.68rem", color: "var(--primary)", border: "1px solid var(--primary)", padding: "0.1rem 0.3rem", borderRadius: "3px", marginLeft: "0.5rem" }}>Uploaded File</span>
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: "0.4rem" }}>
                          <span 
                            onClick={() => handleOpenEditModal(agenda)}
                            style={{ fontWeight: 700, color: "var(--text-primary)", cursor: "pointer" }}
                            className="hover-underline"
                          >
                            {agenda.title}
                          </span>
                          {agenda.archived && (
                            <span style={{ fontSize: "0.68rem", color: "#F59E0B", border: "1px solid #F59E0B", padding: "0.1rem 0.3rem", borderRadius: "3px" }}>
                              Archived
                            </span>
                          )}
                          {!agenda.archived && !agenda.isTemplate && isOlderThanTwoWeeks(agenda.date) && activeTab === 'history' && (
                            <span style={{ fontSize: "0.68rem", color: "var(--text-secondary)", border: "1px solid var(--border-color)", padding: "0.1rem 0.3rem", borderRadius: "3px" }}>
                              Past Meeting
                            </span>
                          )}
                        </span>
                      )}
                      {(agenda.time || agenda.locationName) && (
                        <span style={{ fontSize: "0.72rem", color: "var(--text-secondary)" }}>
                          {agenda.time && `${agenda.time}`} 
                          {agenda.locationName && (
                            <>
                              {" | "}
                              {agenda.locationUrl ? (
                                <a 
                                  href={agenda.locationUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ color: "var(--primary)", textDecoration: "underline" }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {agenda.locationName}
                                </a>
                              ) : (
                                agenda.locationName
                              )}
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{agenda.organizer}</td>
                  <td style={{ textAlign: "center", fontWeight: 600 }}>
                    {agenda.isHistorical ? "N/A" : (agenda.attendeesList ? agenda.attendeesList.length : 0)}
                  </td>
                  <td>
                    {agenda.attachments && agenda.attachments.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                        {agenda.attachments.map(att => (
                          <a
                            key={att.id}
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                              fontSize: "0.72rem", 
                              color: "var(--primary)", 
                              display: "inline-flex", 
                              alignItems: "center", 
                              gap: "0.25rem",
                              textDecoration: "none"
                            }}
                            className="hover-underline"
                          >
                            <FileText size={10} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "120px" }}>
                              {att.name}
                            </span>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", fontStyle: "italic" }}>No files</span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                      {!agenda.isHistorical && (
                        <>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem" }}
                            onClick={() => handlePrintAgenda(agenda)}
                            title="Print Agenda"
                          >
                            <Printer size={14} />
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem" }}
                            onClick={() => handleExportWord(agenda)}
                            title="Export to Word (.doc)"
                          >
                            <Download size={14} />
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem" }}
                            onClick={() => handleDuplicateAgenda(agenda)}
                            title="Duplicate Agenda Template"
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem" }}
                            onClick={() => handleOpenEditModal(agenda)}
                            title="Edit Meeting"
                          >
                            <Edit size={14} />
                          </button>
                          {agenda.archived ? (
                            <button
                              className="btn btn-secondary"
                              style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", color: "var(--accent)" }}
                              onClick={() => handleUnarchiveAgenda(agenda)}
                              title="Restore Agenda to Active"
                            >
                              <ArchiveRestore size={14} />
                            </button>
                          ) : (
                            <button
                              className="btn btn-secondary"
                              style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem" }}
                              onClick={() => handleArchiveAgenda(agenda)}
                              title="Archive Agenda (Move to History)"
                            >
                              <Archive size={14} />
                            </button>
                          )}
                        </>
                      )}
                      
                      {/* Delete check: Active Agendas = always deletable, History = only deletable by Admin */}
                      {(activeTab === "agendas" || isAdmin) ? (
                        <button
                          className="btn btn-danger"
                          style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", backgroundColor: "var(--danger)" }}
                          onClick={() => handleDeleteAgenda(agenda)}
                          title="Delete Meeting"
                        >
                          <Trash2 size={14} />
                        </button>
                      ) : (
                        <button
                          className="btn btn-secondary"
                          style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", opacity: 0.3, cursor: "not-allowed" }}
                          disabled
                          title="Only Admins can delete historical records"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <AgendaModal
          agenda={selectedAgenda}
          onClose={() => setModalOpen(false)}
        />
      )}

      {/* Upload Historical Agenda Modal */}
      {uploadModalOpen && (
        <UploadAgendaModal
          onClose={() => setUploadModalOpen(false)}
        />
      )}
    </div>
  );
}
